import { ThemeProvider, type Theme as ThemeType } from '@emotion/react';
import { useMedia } from '@xo-union/tk-ui-breakpoints';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useState, useContext, useRef, useCallback, useEffect } from 'react';
import { useActiveSinglePageSectionContext } from '../../context/ActiveSinglePageSectionContext';
import { useCurrentRoute } from '../../lib/useCurrentRoute';
import useThemeStyles from '../../lib/useThemeStyles';
import {
  isWwsWeddingWebsiteV2,
  Wws_Page,
  Wws_PhotoAlbum,
} from '../../types/graphql';
import Footer from '../Layout/Footer/Standard/Footer';
import PublishBanner from '../Layout/PublishBanner/PublishBanner';
import Theme from '../Layout/Theme';
import { handleSinglePageSectionScrolling } from '../Templates/SinglePageTemplate';
import { WWContextValue, WWContext } from '../WWContext';
import SinglePageNav from './Nav/SinglePageNav';
import SinglePageCoverImage from './SinglePageCoverImage';
import SinglePageSections from './SinglePageSections';
import SinglePageSplashScreen from './SinglePageSplashScreen';
import styled from '@emotion/styled';

const SinglePageContentsWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  width: 100%;
  position: relative;
  justify-self: center;
  justify-content: center;
  max-width: 2000px;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

interface PagesContainerProps {
  hasCoverImage: boolean;
}

const PagesContainer = styled('div')`
  width: ${({ hasCoverImage }: PagesContainerProps): string =>
    hasCoverImage ? '40%' : '100%'};

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

export interface SinglePageContentsProps {
  themeOverride: string;
  pages: Wws_Page[];
}

const SinglePageContent = ({
  themeOverride,
  pages,
}: SinglePageContentsProps) => {
  const [scrollingTarget, setScrollingTarget] = useState<HTMLElement | null>(
    null
  );
  const { data } = useContext<WWContextValue>(WWContext);
  const { activeSectionId } = useActiveSinglePageSectionContext();

  const isMobile = useMedia('(max-width: 1024px)')?.yes;

  const theme: Partial<ThemeType> = useThemeStyles(themeOverride);

  const mainContentRef = useRef<HTMLDivElement>(null);

  const currentRoute = useCurrentRoute();

  const onScrollingToElementMiss = (
    currentSectionId: string | undefined,
    currentScrollingTarget: HTMLElement | null
  ) => {
    if (!currentScrollingTarget) {
      return;
    }

    if (currentSectionId !== currentScrollingTarget.id) {
      handleSinglePageSectionScrolling(currentScrollingTarget.id);
    }

    setScrollingTarget(null);
  };

  const handleScrollingToElementMiss = useCallback(
    () => onScrollingToElementMiss(activeSectionId, scrollingTarget),
    [activeSectionId, scrollingTarget]
  );

  useEffect(() => {
    addEventListener('scrollend', handleScrollingToElementMiss);
    return () => {
      removeEventListener('scrollend', handleScrollingToElementMiss);
    };
  }, [handleScrollingToElementMiss]);

  useEffect(() => {
    const element = handleSinglePageSectionScrolling(currentRoute);

    setScrollingTarget(element);
  }, [currentRoute]);

  if (!isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    return null;
  }

  const scrollToMainContent = () => {
    if (mainContentRef?.current) {
      mainContentRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const coverImagePath = data?.weddingWebsiteResult.coverPhoto?.path;

  return (
    <ThemeProvider theme={theme}>
      <SinglePageContentsWrapper>
        {!coverImagePath && <SinglePageNav pages={pages as Wws_Page[]} />}
        {coverImagePath && (
          <SinglePageCoverImage
            coverImagePath={coverImagePath}
            weddingWebsiteResult={data?.weddingWebsiteResult}
          >
            <SinglePageNav pages={pages as Wws_Page[]} />
          </SinglePageCoverImage>
        )}
        <PagesContainer
          hasCoverImage={!!coverImagePath}
          className={coverImagePath ? 'narrowColumn' : undefined}
        >
          <PublishBanner />
          <Theme
            footer={<Footer isSinglePageView />}
            offsetBackgroundForNavBar={false}
          >
            <SinglePageSplashScreen
              themeOverride={themeOverride}
              onScrollDown={scrollToMainContent}
              isMobile={isMobile}
            />
            <div ref={mainContentRef}>
              <SinglePageSections
                isMobile={isMobile}
                pages={pages as Wws_Page[]}
                photoAlbums={
                  data?.weddingWebsiteResult?.photoAlbums as Maybe<
                    Wws_PhotoAlbum[]
                  >
                }
              />
            </div>
          </Theme>
        </PagesContainer>
      </SinglePageContentsWrapper>
    </ThemeProvider>
  );
};

export default SinglePageContent;
